import {useEffect, useRef, useState} from 'react';

// The photo block on tell detail: horizontal scroll-snap pages with dots. No dependency.
// The frame takes the first photo's shape (width / height), held between a gentle portrait (4:5)
// and a wide landscape (16:10) and never taller than about two thirds of the screen, so a
// portrait shot is not cropped to a letterbox and a landscape one is shown whole.
export interface PagerPhoto {
    key: string;
    url: string;
    /** A preview still uploading, drawn with a badge. */
    uploading?: boolean;
    failed?: boolean;
}

const TALLEST = 4 / 5;
const WIDEST = 16 / 10;

export default function PhotoPager({photos, ratio = 4 / 3}: {photos: PagerPhoto[]; ratio?: number}) {
    const track = useRef<HTMLDivElement>(null);
    const [index, setIndex] = useState(0);

    useEffect(() => {
        const node = track.current;
        if (!node) return;
        const onScroll = () => setIndex(Math.round(node.scrollLeft / Math.max(1, node.clientWidth)));
        node.addEventListener('scroll', onScroll, {passive: true});
        return () => node.removeEventListener('scroll', onScroll);
    }, []);

    if (photos.length === 0) return null;

    return (
        <div className="relative max-h-[66svh] w-full bg-sunken" style={{aspectRatio: Math.min(WIDEST, Math.max(TALLEST, ratio))}}>
            <div ref={track} className="flex h-full snap-x snap-mandatory overflow-x-auto [scrollbar-width:none]">
                {photos.map(photo => (
                    <div key={photo.key} className="relative h-full w-full shrink-0 snap-center">
                        <img src={photo.url} alt="" className={`h-full w-full object-cover ${photo.uploading ? 'opacity-70' : ''}`} draggable={false} />
                        {photo.uploading && <span className="absolute bottom-12 left-3 rounded-pill bg-scrim/55 px-3 py-1 text-xs font-semibold text-white">Uploading…</span>}
                        {photo.failed && <span className="absolute bottom-12 left-3 rounded-pill bg-rust px-3 py-1 text-xs font-semibold text-white">Didn&rsquo;t upload</span>}
                    </div>
                ))}
            </div>
            {photos.length > 1 && (
                <div className="pointer-events-none absolute bottom-10 left-0 right-0 flex justify-center gap-[6px]" aria-label={`Photo ${index + 1} of ${photos.length}`}>
                    {photos.map((p, i) => (
                        <span key={p.key} className={`h-2 w-2 rounded-pill ${i === index ? 'bg-white' : 'bg-white/50'}`} />
                    ))}
                </div>
            )}
        </div>
    );
}
